import { Bot, Lock, MapPin, MessageSquareText, BarChart3, Workflow } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';

const features = [
  {
    icon: MessageSquareText,
    title: 'Ask payroll in plain English',
    body: 'Query leave balances, NPF/NSF, overtime, and employee records without opening another report.',
  },
  {
    icon: BarChart3,
    title: 'AI-powered reports',
    body: 'Generate payroll insights and export-ready reports with natural language prompts.',
  },
  {
    icon: Workflow,
    title: 'Automate the busywork',
    body: 'Trigger workflows when payroll is generated or finalised so nothing slips through.',
  },
];

const trustPoints = [
  { icon: Lock, label: 'Private AI — your data stays employer-scoped' },
  { icon: MapPin, label: 'Built for Mauritius payroll & compliance' },
  { icon: Bot, label: 'Works inside your Payroll Mauritius workspace' },
];

export function LoginMarketing() {
  return (
    <div className="relative flex h-full min-h-full flex-col overflow-hidden bg-[#071a28] text-white">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 10% 0%, rgba(43,130,234,0.22), transparent 55%), radial-gradient(ellipse 70% 50% at 90% 100%, rgba(244,63,94,0.12), transparent 50%), linear-gradient(165deg, #071a28 0%, #0c2a3d 50%, #0a2233 100%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'linear-gradient(to bottom, black 0%, transparent 85%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 top-24 h-72 w-72 rounded-full border border-[#2b82ea]/25"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-8 top-40 h-48 w-48 rounded-full border border-[#f43f5e]/15"
      />

      <div className="relative z-10 flex flex-1 flex-col justify-between px-8 py-10 lg:px-12 lg:py-12 xl:px-16">
        <header>
          <div className="animate-fade-up">
            <BrandLogo
              className="h-14 w-auto max-w-[min(100%,20rem)] sm:h-16 xl:h-[4.5rem]"
              alt="Payroll Mauritius"
            />
          </div>
          <p
            className="mt-5 inline-flex animate-fade-up items-center rounded-md bg-white/5 px-2.5 py-1 text-xs font-medium uppercase tracking-[0.18em] text-[#7eb6f5]"
            style={{ animationDelay: '60ms' }}
          >
            Private AI · Payroll-GPT
          </p>
          <h1
            className="mt-4 max-w-lg animate-fade-up font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl"
            style={{ animationDelay: '100ms' }}
          >
            Private AI for Payroll Mauritius
          </h1>
          <p
            className="mt-3 max-w-md animate-fade-up text-lg leading-relaxed text-white/70"
            style={{ animationDelay: '140ms' }}
          >
            Chat, reports, and automation in one secure workspace — scoped to your employer.
          </p>

          <div
            className="mt-8 flex animate-fade-up flex-wrap gap-x-6 gap-y-3"
            style={{ animationDelay: '200ms' }}
          >
            {trustPoints.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-sm text-white/60">
                <Icon className="h-4 w-4 shrink-0 text-[#2b82ea]" aria-hidden />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </header>

        <section className="mt-12" aria-label="What you can do with Payroll-GPT">
          <div className="grid gap-4 sm:grid-cols-3">
            {features.map(({ icon: Icon, title, body }, index) => (
              <article
                key={title}
                className="group animate-fade-up rounded-xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm transition-colors hover:border-[#2b82ea]/40 hover:bg-white/[0.07]"
                style={{ animationDelay: `${120 + index * 100}ms` }}
              >
                <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-[#2b82ea]/15 text-[#2b82ea] transition-transform duration-300 group-hover:scale-105">
                  <Icon className="h-4 w-4" aria-hidden />
                </div>
                <h2 className="font-display text-base font-medium tracking-tight">{title}</h2>
                <p className="mt-1.5 text-sm leading-relaxed text-white/55">{body}</p>
              </article>
            ))}
          </div>
        </section>

        <footer className="mt-10 border-t border-white/10 pt-6">
          <p className="text-sm text-white/45">
            Powered by <span className="font-medium text-white/70">Payroll Mauritius</span> — HR &
            payroll platform. New customers can register at{' '}
            <a
              href="https://payrollmauritius.com"
              className="font-medium text-[#7eb6f5] underline-offset-2 hover:underline"
              rel="noopener noreferrer"
            >
              payrollmauritius.com
            </a>
            .
          </p>
        </footer>
      </div>
    </div>
  );
}
